import { useState, useEffect, useCallback } from "react";

/* ═══════════════════════════════════════════════════════
   DESIGN TOKENS — Valve Finder Light Theme
═══════════════════════════════════════════════════════ */
const C = {
  // Backgrounds
  bg: "#f0f7f1",
  surface: "#ffffff",
  card: "#ffffff",
  cardAlt: "#f7fbf7",
  // Borders
  border: "#ddeedd",
  borderLight: "#eef6ee",
  // Brand greens
  primary: "#2e7d32",
  primaryLight: "#e8f5e9",
  primaryMid: "#4caf50",
  // Text
  text: "#1a2e1e",
  textSub: "#4a6a50",
  textMuted: "#7a9a80",
  textFaint: "#b0ccb4",
  // Accents
  blue: "#1565c0",
  blueBg: "#e3f2fd",
  orange: "#e65100",
  orangeBg: "#fff3e0",
  teal: "#00695c",
  tealBg: "#e0f2f1",
  purple: "#6a1b9a",
  purpleBg: "#f3e5f5",
  red: "#c62828",
  redBg: "#ffebee",
  // Shadows
  shadow: "0 2px 12px rgba(46,125,50,0.08)",
  shadowMd: "0 4px 20px rgba(46,125,50,0.12)",
};

/* ═══════════════════════════════════════════════════════
   STORAGE & UTILITIES
═══════════════════════════════════════════════════════ */
const STORAGE_KEY = "agro-manager-v2";
const P_TO_P2O5 = 2.27;
const K_TO_K2O = 1.20;

const DEFAULT_STATE = {
  courses: [],
  selectedCourseId: null,
  chemicals: [],
  sprayRecords: [],
  fertRecords: [],
  gpRecords: [],
};

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    // migrate from v1
    const v1 = localStorage.getItem("agro-manager-v1");
    if (!raw && v1) {
      const old = JSON.parse(v1);
      return { ...DEFAULT_STATE, ...old, gpRecords: [] };
    }
    return raw ? { ...DEFAULT_STATE, ...JSON.parse(raw) } : DEFAULT_STATE;
  } catch { return DEFAULT_STATE; }
}

function saveState(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
}

const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
const toNum = (v) => parseFloat(v) || 0;
const fmt = (n, d = 1) => {
  if (n === null || n === undefined || isNaN(n)) return "-";
  return n.toLocaleString("ko-KR", { maximumFractionDigits: d, minimumFractionDigits: d > 0 ? 0 : 0 });
};
const todayStr = () => new Date().toISOString().slice(0, 10);

function downloadCSV(filename, rows) {
  const bom = "\uFEFF";
  const csv = rows.map(r =>
    r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")
  ).join("\n");
  const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/* ═══════════════════════════════════════════════════════
   GROWTH POTENTIAL FORMULAS
   Cool season (한지형): Topt=20, σ=5.5  (fescue, bentgrass, bluegrass)
   Warm season (난지형): Topt=31, σ=7.0  (bermuda, zoysiagrass, seashore paspalum)
═══════════════════════════════════════════════════════ */
function calcGP(temp, topt, sigma) {
  const gp = Math.exp(-0.5 * Math.pow((temp - topt) / sigma, 2));
  return Math.round(gp * 100);
}
const gpCool = (t) => calcGP(t, 20, 5.5);  // 한지형 (벤트그라스·크리핑벤트·페스큐·켄터키블루)
const gpWarm = (t) => calcGP(t, 31, 7.0);  // 난지형 (버뮤다·조이시아·씨쇼어파스팔럼)

function gpColor(pct) {
  if (pct >= 70) return { text: C.primary, bg: C.primaryLight };
  if (pct >= 40) return { text: "#827717", bg: "#f9fbe7" };
  if (pct >= 15) return { text: C.orange, bg: C.orangeBg };
  return { text: C.red, bg: C.redBg };
}
function gpLabel(pct) {
  if (pct >= 70) return "최적 생장";
  if (pct >= 40) return "양호";
  if (pct >= 15) return "제한적";
  return "생장 불가";
}

/* ═══════════════════════════════════════════════════════
   SHARED STYLES
═══════════════════════════════════════════════════════ */
const S = {
  app: {
    fontFamily: "'Noto Sans KR', 'Apple SD Gothic Neo', sans-serif",
    background: C.bg,
    color: C.text,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    maxWidth: 480,
    margin: "0 auto",
    position: "relative",
  },
  // Header
  header: {
    background: C.surface,
    borderBottom: `1px solid ${C.border}`,
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "sticky",
    top: 0,
    zIndex: 100,
    boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
  },
  headerLogo: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  headerIcon: {
    width: 34,
    height: 34,
    background: C.primaryLight,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: C.primary,
    letterSpacing: "-0.3px",
    lineHeight: 1.1,
  },
  headerSub: { fontSize: 10, color: C.textMuted, fontWeight: 500 },
  courseSelect: {
    background: C.primaryLight,
    border: `1px solid ${C.border}`,
    color: C.primary,
    padding: "6px 10px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
    maxWidth: 140,
    outline: "none",
    cursor: "pointer",
  },
  // Main
  main: { flex: 1, overflowY: "auto", padding: "14px 14px 88px" },
  // Bottom Nav
  nav: {
    position: "fixed",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "100%",
    maxWidth: 480,
    background: C.surface,
    borderTop: `1px solid ${C.border}`,
    display: "flex",
    zIndex: 200,
    paddingBottom: "env(safe-area-inset-bottom)",
    boxShadow: "0 -2px 12px rgba(0,0,0,0.06)",
  },
  navBtn: {
    flex: 1,
    background: "none",
    border: "none",
    color: C.textMuted,
    padding: "10px 4px 8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 3,
    cursor: "pointer",
    transition: "color 0.15s",
  },
  navIcon: { fontSize: 20, lineHeight: 1 },
  navLabel: { fontSize: 10, fontWeight: 600 },
  // Cards
  card: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    padding: 16,
    marginBottom: 10,
    boxShadow: C.shadow,
  },
  cardTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: C.textMuted,
    marginBottom: 14,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },
  // Inputs
  input: {
    background: C.bg,
    border: `1.5px solid ${C.border}`,
    color: C.text,
    padding: "10px 12px",
    borderRadius: 10,
    fontSize: 14,
    width: "100%",
    boxSizing: "border-box",
    outline: "none",
    transition: "border-color 0.15s",
  },
  label: {
    fontSize: 11,
    color: C.textSub,
    display: "block",
    marginBottom: 5,
    fontWeight: 700,
  },
  formGroup: { marginBottom: 12 },
  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 },
  row3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 },
  // Buttons
  btnPrimary: {
    background: C.primary,
    color: "#fff",
    border: "none",
    padding: "12px 20px",
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    width: "100%",
    boxShadow: "0 2px 8px rgba(46,125,50,0.25)",
  },
  btnSecondary: {
    background: "transparent",
    color: C.primary,
    border: `1.5px solid ${C.primary}`,
    padding: "9px 14px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnDanger: {
    background: "transparent",
    color: C.red,
    border: `1px solid ${C.red}`,
    padding: "7px 10px",
    borderRadius: 8,
    fontSize: 12,
    cursor: "pointer",
  },
  // Result
  resultBox: {
    background: C.primaryLight,
    border: `1.5px solid #c8e6c9`,
    borderRadius: 12,
    padding: 14,
    marginTop: 14,
  },
  resultRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "7px 0",
    borderBottom: `1px solid ${C.borderLight}`,
  },
  resultLabel: { fontSize: 13, color: C.textSub },
  resultValue: { fontSize: 15, fontWeight: 800, color: C.primary },
  // Tabs
  subTabs: { display: "flex", gap: 8, marginBottom: 14 },
  subTab: {
    flex: 1,
    padding: "9px 0",
    background: C.surface,
    border: `1.5px solid ${C.border}`,
    color: C.textMuted,
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "center",
  },
  subTabActive: {
    background: C.primary,
    border: `1.5px solid ${C.primary}`,
    color: "#fff",
  },
  // Feature card (Valve Finder style)
  featureCard: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    padding: "18px 16px",
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: 14,
    cursor: "pointer",
    boxShadow: C.shadow,
    transition: "transform 0.12s, box-shadow 0.12s",
    WebkitTapHighlightColor: "transparent",
  },
  featureIcon: {
    width: 52,
    height: 52,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    flexShrink: 0,
  },
  // List item
  listItem: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: "12px 14px",
    marginBottom: 8,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  // Modal
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    zIndex: 1000,
    backdropFilter: "blur(2px)",
  },
  modal: {
    background: C.surface,
    borderTop: `2px solid ${C.border}`,
    borderRadius: "20px 20px 0 0",
    padding: "20px 16px 32px",
    width: "100%",
    maxWidth: 480,
    maxHeight: "90vh",
    overflowY: "auto",
  },
  // Badge
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
  },
};

/* ═══════════════════════════════════════════════════════
   SHARED COMPONENTS
═══════════════════════════════════════════════════════ */
function Modal({ title, onClose, children }) {
  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <span style={{ fontSize: 17, fontWeight: 800, color: C.text }}>{title}</span>
          <button onClick={onClose} style={{ background: C.bg, border: "none", color: C.textMuted, fontSize: 18, cursor: "pointer", borderRadius: 8, width: 32, height: 32 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SectionLabel({ label }) {
  return <div style={S.cardTitle}>{label}</div>;
}

function NoCourse({ onGoSettings }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div style={{ fontSize: 48, marginBottom: 14 }}>⛳</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8 }}>골프장을 선택해 주세요</div>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 24 }}>설정 탭에서 골프장을 추가하고 선택하세요</div>
      {onGoSettings && (
        <button onClick={onGoSettings} style={{ ...S.btnPrimary, width: "auto", padding: "11px 28px" }}>설정으로 이동</button>
      )}
    </div>
  );
}

function EmptyMsg({ msg }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 20px", color: C.textFaint }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
      <div style={{ fontSize: 13 }}>{msg}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   HOME SCREEN — Valve Finder Style
═══════════════════════════════════════════════════════ */
function HomeScreen({ course, courseChemicals, courseSprayRecords, courseFertRecords, courseGpRecords, setTab }) {
  if (!course) {
    return (
      <div style={{ textAlign: "center", padding: "70px 20px 40px" }}>
        <div style={{ width: 80, height: 80, background: C.primaryLight, borderRadius: 24, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", overflow: "hidden" }}>
          <img src="/icons/grasslogo.png" alt="잔디 로고" style={{ width: 60, height: 60, objectFit: "contain" }} />
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 8 }}>시비·시약·생장가능지수 통합 관리</div>
        <div style={{ color: C.textMuted, fontSize: 14, lineHeight: 1.8, marginBottom: 28 }}>
          골프장 시비·시약 통합 관리 시스템<br />설정 탭에서 골프장을 추가해 주세요
        </div>
        <button onClick={() => setTab("settings")} style={{ ...S.btnPrimary, width: "auto", padding: "12px 32px" }}>
          시작하기 →
        </button>
        <div style={{ marginTop: 32, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, color: C.textMuted, fontSize: 13, fontWeight: 600 }}>
          <img src="/icons/logo_goldentree1.png" alt="서원양행" style={{ height: 22, objectFit: "contain" }} />
          시비·시약 관리 v2.0 · (주)서원양행
        </div>
      </div>
    );
  }

  const lowStock = courseChemicals.filter(c => c.minStock && Number(c.stock) <= Number(c.minStock));
  const thisMonth = todayStr().slice(0, 7);
  const monthlySpray = courseSprayRecords.filter(r => r.date?.startsWith(thisMonth)).length;
  const monthlyFert = courseFertRecords.filter(r => r.date?.startsWith(thisMonth)).length;
  const totalRecords = courseSprayRecords.length + courseFertRecords.length;
  const monthlyTotal = monthlySpray + monthlyFert;

  // Last GP
  const lastGp = [...courseGpRecords].sort((a, b) => b.date.localeCompare(a.date))[0];

  const features = [
    {
      icon: "🧪",
      bg: C.primaryLight,
      title: "희석액 살포 계산",
      sub: "원액 · 배율 · 면적 → 살포량",
      action: () => setTab("calc"),
      accent: C.primary,
    },
    {
      icon: "🌿",
      bg: C.tealBg,
      title: "시비량 계산",
      sub: "N · P · K 기준 비료 필요량",
      action: () => setTab("calc"),
      accent: C.teal,
    },
    {
      icon: "📈",
      bg: C.blueBg,
      title: "생장가능지수",
      sub: "한지형 · 난지형 GP 계산",
      action: () => setTab("gp"),
      accent: C.blue,
    },
    {
      icon: "📋",
      bg: C.orangeBg,
      title: "시비·시약 이력 조회",
      sub: "살포 · 시비 기록 · CSV 내보내기",
      action: () => setTab("history"),
      accent: C.orange,
    },
    {
      icon: "📦",
      bg: C.purpleBg,
      title: "비료·농약 재고",
      sub: "재고 관리 · 부족 알림",
      action: () => setTab("inventory"),
      accent: C.purple,
    },
  ];

  return (
    <div>
      {/* Course Header */}
      <div style={{
        background: `linear-gradient(135deg, ${C.primary} 0%, #1b5e20 100%)`,
        borderRadius: 18,
        padding: "20px 18px",
        marginBottom: 12,
        color: "#fff",
        boxShadow: "0 4px 20px rgba(46,125,50,0.3)",
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, letterSpacing: "1px", marginBottom: 4 }}>GOLF COURSE</div>
        <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 2 }}>{course.name}</div>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 16 }}>
          관리 면적: {course.area ? `${Number(course.area).toLocaleString()} m²` : "미설정"} · {new Date().toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric", weekday: "short" })}
        </div>

        {/* Stats Row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {[
            { label: "비료·농약", val: courseChemicals.length, unit: "종" },
            { label: "전체 이력", val: totalRecords, unit: "건" },
            { label: "이번달", val: monthlyTotal, unit: "건" },
          ].map(st => (
            <div key={st.label} style={{ background: "rgba(255,255,255,0.15)", borderRadius: 12, padding: "10px 8px", textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{st.val}</div>
              <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>{st.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Low stock alert */}
      {lowStock.length > 0 && (
        <div style={{ ...S.card, border: `1.5px solid #ffcc02`, background: C.orangeBg, padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 16 }}>⚠️</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.orange }}>재고 부족 알림 {lowStock.length}건</span>
          </div>
          {lowStock.map(c => (
            <div key={c.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: C.textSub }}>
              <span style={{ fontWeight: 600 }}>{c.name}</span>
              <span style={{ color: C.orange }}>{c.stock} {c.unit} (최소 {c.minStock})</span>
            </div>
          ))}
        </div>
      )}

      {/* Last GP quick view */}
      {lastGp && (
        <div style={{ ...S.card, cursor: "pointer" }} onClick={() => setTab("gp")}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 4 }}>최근 생장가능지수 · {lastGp.date}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>온도 {lastGp.temp}°C</div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 2 }}>한지형</div>
                <div style={{ ...S.badge, background: gpColor(lastGp.coolGp).bg, color: gpColor(lastGp.coolGp).text, fontSize: 13, fontWeight: 800 }}>{lastGp.coolGp}%</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 2 }}>난지형</div>
                <div style={{ ...S.badge, background: gpColor(lastGp.warmGp).bg, color: gpColor(lastGp.warmGp).text, fontSize: 13, fontWeight: 800 }}>{lastGp.warmGp}%</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feature Cards */}
      <div style={{ marginTop: 4 }}>
        {features.map((f, i) => (
          <div
            key={i}
            style={S.featureCard}
            onClick={f.action}
            onTouchStart={e => e.currentTarget.style.transform = "scale(0.98)"}
            onTouchEnd={e => e.currentTarget.style.transform = "scale(1)"}
          >
            <div style={{ ...S.featureIcon, background: f.bg }}>
              {f.icon}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: f.accent, marginBottom: 3 }}>{f.title}</div>
              <div style={{ fontSize: 12, color: C.textMuted }}>{f.sub}</div>
            </div>
            <div style={{ fontSize: 18, color: C.textFaint }}>›</div>
          </div>
        ))}
      </div>

      <div style={{ textAlign: "center", color: C.textFaint, fontSize: 11, marginTop: 8, paddingBottom: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
        <img src="/icons/logo_goldentree1.png" alt="서원양행" style={{ height: 16, objectFit: "contain" }} />
        시비·시약 관리 v2.0 · (주)서원양행
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   GROWTH POTENTIAL SCREEN
═══════════════════════════════════════════════════════ */
function GpScreen({ state, upd, course }) {
  const [temp, setTemp] = useState("");
  const [result, setResult] = useState(null);
  const [date, setDate] = useState(todayStr());
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState(false);
  const [histTab, setHistTab] = useState("calc"); // "calc" | "history"

  const courseGpRecords = state.gpRecords?.filter(r => r.courseId === state.selectedCourseId) || [];

  const calc = () => {
    const t = toNum(temp);
    if (temp === "" || isNaN(t)) { alert("온도를 입력해 주세요"); return; }
    const cool = gpCool(t);
    const warm = gpWarm(t);
    setResult({ temp: t, coolGp: cool, warmGp: warm });
    setSaved(false);
  };

  const saveRecord = () => {
    if (!result || !state.selectedCourseId) return;
    upd({ gpRecords: [...(state.gpRecords || []), { id: genId(), courseId: state.selectedCourseId, date, temp: result.temp, coolGp: result.coolGp, warmGp: result.warmGp, note }] });
    setSaved(true);
  };

  const delGp = (id) => upd({ gpRecords: (state.gpRecords || []).filter(r => r.id !== id) });

  const exportGp = () => {
    downloadCSV(`gp_${todayStr()}.csv`, [
      ["날짜", "온도(°C)", "한지형GP(%)", "난지형GP(%)", "판정(한지형)", "판정(난지형)", "메모"],
      ...courseGpRecords.sort((a, b) => b.date.localeCompare(a.date)).map(r =>
        [r.date, r.temp, r.coolGp, r.warmGp, gpLabel(r.coolGp), gpLabel(r.warmGp), r.note]),
    ]);
  };

  // Temp reference table
  const refTemps = [0, 5, 10, 15, 20, 25, 30, 35, 40];

  return (
    <div>
      <div style={S.subTabs}>
        {[["calc", "📈 GP 계산"], ["table", "📊 참조 테이블"], ["history", "📋 GP 이력"]].map(([id, label]) => (
          <button key={id} onClick={() => setHistTab(id)}
            style={{ ...S.subTab, ...(histTab === id ? S.subTabActive : {}), fontSize: 11 }}>
            {label}
          </button>
        ))}
      </div>

      {histTab === "calc" && (
        <div>
          {/* Formula info */}
          <div style={{ ...S.card, background: C.blueBg, border: `1px solid #bbdefb`, padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.blue, marginBottom: 6 }}>📐 생장가능지수 (GP) 공식</div>
            <div style={{ fontSize: 11, color: C.textSub, lineHeight: 1.8 }}>
              GP = exp(−0.5 × ((T − Topt) / σ)²) × 100%<br />
              <b>한지형</b> Topt=20°C, σ=5.5 · <b>난지형</b> Topt=31°C, σ=7.0<br />
              <span style={{ color: C.textMuted }}>출처: PACE Turf / Wenninghoff GP Model</span>
            </div>
          </div>

          <div style={S.card}>
            <div style={S.formGroup}>
              <label style={S.label}>날짜</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={S.input} />
            </div>

            <div style={S.formGroup}>
              <label style={S.label}>현재 기온 (°C)</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="예: 22"
                  value={temp}
                  onChange={e => { setTemp(e.target.value); setResult(null); setSaved(false); }}
                  style={{ ...S.input, fontSize: 20, fontWeight: 700, textAlign: "center" }}
                />
                <button onClick={calc} style={{ ...S.btnPrimary, width: "auto", padding: "10px 20px", whiteSpace: "nowrap" }}>
                  계산
                </button>
              </div>
            </div>

            {/* Slider */}
            <div style={S.formGroup}>
              <label style={S.label}>슬라이더 조절 (-10 ~ 45°C)</label>
              <input
                type="range" min="-10" max="45" step="0.5"
                value={temp || 20}
                onChange={e => { setTemp(e.target.value); setResult(null); setSaved(false); }}
                style={{ width: "100%", accentColor: C.primary }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.textFaint, marginTop: 2 }}>
                <span>-10°C</span><span>0°C</span><span>10°C</span><span>20°C</span><span>30°C</span><span>40°C</span><span>45°C</span>
              </div>
            </div>

            {result && (
              <div style={S.resultBox}>
                <div style={{ textAlign: "center", fontSize: 32, fontWeight: 900, color: C.text, marginBottom: 4 }}>
                  {result.temp}°C
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                  {[
                    { label: "한지형 (Cool)", gp: result.coolGp, sub: "벤트·크리핑벤트·페스큐·블루그라스" },
                    { label: "난지형 (Warm)", gp: result.warmGp, sub: "조이시아·버뮤다·씨쇼어파스팔럼" },
                  ].map(({ label, gp, sub }) => {
                    const col = gpColor(gp);
                    return (
                      <div key={label} style={{ background: col.bg, borderRadius: 12, padding: "14px 10px", textAlign: "center", border: `1.5px solid ${col.text}33` }}>
                        <div style={{ fontSize: 11, color: col.text, fontWeight: 700, marginBottom: 4 }}>{label}</div>
                        <div style={{ fontSize: 36, fontWeight: 900, color: col.text, lineHeight: 1 }}>{gp}%</div>
                        <div style={{ fontSize: 11, color: col.text, marginTop: 4, fontWeight: 600 }}>{gpLabel(gp)}</div>
                        <div style={{ fontSize: 10, color: C.textMuted, marginTop: 4 }}>{sub}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Advice */}
                <div style={{ background: "rgba(255,255,255,0.7)", borderRadius: 10, padding: "10px 12px", fontSize: 12, color: C.textSub, lineHeight: 1.7 }}>
                  {result.coolGp >= 70 && result.warmGp >= 70
                    ? "✅ 한지형·난지형 모두 최적 생장 조건입니다."
                    : result.coolGp >= 70
                      ? "🌿 한지형 최적 생장. 난지형 잔디 시비는 효과가 제한적입니다."
                      : result.warmGp >= 70
                        ? "☀️ 난지형 최적 생장. 한지형 잔디 관리에 주의하세요."
                        : result.coolGp < 15 && result.warmGp < 15
                          ? "❄️ 생장이 거의 불가능한 온도입니다. 시비 효과가 매우 낮습니다."
                          : `⚠️ 한지형 ${gpLabel(result.coolGp)}, 난지형 ${gpLabel(result.warmGp)} 상태입니다.`}
                </div>

                <div style={S.formGroup}>
                  <label style={{ ...S.label, marginTop: 12 }}>메모 (선택)</label>
                  <input placeholder="날씨, 코스명 등" value={note} onChange={e => setNote(e.target.value)} style={S.input} />
                </div>

                {!course ? (
                  <div style={{ color: C.orange, fontSize: 12, textAlign: "center", padding: 8 }}>골프장을 선택해야 저장할 수 있습니다</div>
                ) : saved ? (
                  <div style={{ color: C.primary, fontSize: 13, textAlign: "center", padding: 8, fontWeight: 700 }}>✅ 이력에 저장되었습니다</div>
                ) : (
                  <button onClick={saveRecord} style={S.btnPrimary}>이력 저장</button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {histTab === "table" && (
        <div style={S.card}>
          <SectionLabel label="온도별 생장가능지수 참조표" />
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.bg }}>
                  <th style={{ padding: "8px 10px", textAlign: "left", color: C.textSub, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>온도(°C)</th>
                  <th style={{ padding: "8px 10px", textAlign: "center", color: C.teal, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>한지형</th>
                  <th style={{ padding: "8px 10px", textAlign: "center", color: C.orange, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>난지형</th>
                </tr>
              </thead>
              <tbody>
                {refTemps.map(t => {
                  const cool = gpCool(t);
                  const warm = gpWarm(t);
                  const coolCol = gpColor(cool);
                  const warmCol = gpColor(warm);
                  return (
                    <tr key={t} style={{ borderBottom: `1px solid ${C.borderLight}` }}>
                      <td style={{ padding: "8px 10px", fontWeight: 700, color: C.text }}>{t}°C</td>
                      <td style={{ padding: "8px 10px", textAlign: "center" }}>
                        <span style={{ ...S.badge, background: coolCol.bg, color: coolCol.text }}>{cool}%</span>
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "center" }}>
                        <span style={{ ...S.badge, background: warmCol.bg, color: warmCol.text }}>{warm}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: C.textFaint, marginTop: 12, lineHeight: 1.6 }}>
            한지형 최적: 20°C · 난지형 최적: 31°C
          </div>
        </div>
      )}

      {histTab === "history" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 13, color: C.textMuted }}>총 {courseGpRecords.length}건</span>
            <button onClick={exportGp} style={S.btnSecondary}>CSV 내보내기</button>
          </div>
          {!course ? <NoCourse /> :
            courseGpRecords.length === 0 ? <EmptyMsg msg="GP 이력이 없습니다" /> :
              [...courseGpRecords].sort((a, b) => b.date.localeCompare(a.date)).map(r => (
                <div key={r.id} style={S.listItem}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{r.temp}°C</div>
                      <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{r.date}</div>
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <div style={{ ...S.badge, background: gpColor(r.coolGp).bg, color: gpColor(r.coolGp).text }}>한 {r.coolGp}%</div>
                      <div style={{ ...S.badge, background: gpColor(r.warmGp).bg, color: gpColor(r.warmGp).text }}>난 {r.warmGp}%</div>
                      <button onClick={() => delGp(r.id)} style={{ ...S.btnDanger, padding: "4px 7px" }}>🗑</button>
                    </div>
                  </div>
                  {r.note && <div style={{ fontSize: 11, color: C.textFaint, marginTop: 4 }}>{r.note}</div>}
                </div>
              ))
          }
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   SPRAY CALCULATOR
═══════════════════════════════════════════════════════ */
function SprayCalc({ state, upd, course, courseChemicals }) {
  const emptyForm = () => ({ date: todayStr(), chemicalId: "", chemicalName: "", concentration: "", ratio: "", area: course?.area || "", note: "" });
  const [f, setF] = useState(emptyForm);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const chemicals = courseChemicals.filter(c => c.type === "chemical" || c.type === "both");
  const setField = (k, v) => setF(p => ({ ...p, [k]: v }));

  const calc = () => {
    const conc = toNum(f.concentration), ratio = toNum(f.ratio), area = toNum(f.area);
    if (!conc || !ratio || !area) { alert("원액 농도, 희석 배율, 살포 면적을 입력해 주세요"); return; }
    const origL = (conc * area) / 1000;
    setResult({ origL, waterL: origL * (ratio - 1), totalL: origL * ratio, conc, ratio, area });
    setSaved(false);
  };

  const saveRecord = () => {
    if (!result || !state.selectedCourseId) return;
    const chemName = f.chemicalId ? (courseChemicals.find(c => c.id === f.chemicalId)?.name || "") : f.chemicalName;
    upd({ sprayRecords: [...state.sprayRecords, { id: genId(), courseId: state.selectedCourseId, date: f.date, chemicalId: f.chemicalId, chemicalName: chemName, concentration: result.conc, ratio: result.ratio, area: result.area, origL: result.origL, waterL: result.waterL, totalDilution: result.totalL, note: f.note }] });
    setSaved(true);
  };

  return (
    <div style={S.card}>
      <SectionLabel label="💧 희석액 살포량 계산" />
      <div style={S.formGroup}>
        <label style={S.label}>날짜</label>
        <input type="date" value={f.date} onChange={e => setField("date", e.target.value)} style={S.input} />
      </div>
      <div style={S.formGroup}>
        <label style={S.label}>농약 선택</label>
        <select value={f.chemicalId} onChange={e => {
          const c = chemicals.find(x => x.id === e.target.value);
          setF(p => ({ ...p, chemicalId: e.target.value, chemicalName: c?.name || "" }));
        }} style={S.input}>
          <option value="">직접 입력</option>
          {chemicals.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {!f.chemicalId && (
          <input placeholder="농약명 직접 입력" value={f.chemicalName}
            onChange={e => setField("chemicalName", e.target.value)} style={{ ...S.input, marginTop: 6 }} />
        )}
      </div>
      <div style={S.row2}>
        <div>
          <label style={S.label}>원액 농도 (mL/m²)</label>
          <input type="number" inputMode="decimal" placeholder="예: 20" value={f.concentration} onChange={e => setField("concentration", e.target.value)} style={S.input} />
        </div>
        <div>
          <label style={S.label}>희석 배율</label>
          <input type="number" inputMode="decimal" placeholder="예: 1000" value={f.ratio} onChange={e => setField("ratio", e.target.value)} style={S.input} />
        </div>
      </div>
      <div style={S.formGroup}>
        <label style={S.label}>살포 면적 (m²)</label>
        <input type="number" inputMode="decimal" placeholder="예: 1000" value={f.area} onChange={e => setField("area", e.target.value)} style={S.input} />
      </div>
      <div style={S.formGroup}>
        <label style={S.label}>메모</label>
        <input placeholder="메모 (선택)" value={f.note} onChange={e => setField("note", e.target.value)} style={S.input} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={calc} style={S.btnPrimary}>계산하기</button>
        <button onClick={() => { setF(emptyForm()); setResult(null); setSaved(false); }} style={{ ...S.btnSecondary, whiteSpace: "nowrap" }}>초기화</button>
      </div>
      {result && (
        <div style={S.resultBox}>
          {[["원액량", `${fmt(result.origL, 2)} L`], ["필요 물량", `${fmt(result.waterL, 1)} L`], ["총 희석액", `${fmt(result.totalL, 1)} L`]].map(([l, v], i, a) => (
            <div key={l} style={{ ...S.resultRow, ...(i === a.length - 1 ? { borderBottom: "none" } : {}) }}>
              <span style={S.resultLabel}>{l}</span>
              <span style={S.resultValue}>{v}</span>
            </div>
          ))}
          <div style={{ marginTop: 12 }}>
            {!course ? <div style={{ color: C.orange, fontSize: 12, textAlign: "center" }}>골프장을 선택해야 저장할 수 있습니다</div>
              : saved ? <div style={{ color: C.primary, fontSize: 13, textAlign: "center", fontWeight: 700 }}>✅ 저장되었습니다</div>
                : <button onClick={saveRecord} style={{ ...S.btnSecondary, width: "100%" }}>이력 저장</button>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   FERTILIZER CALCULATOR
═══════════════════════════════════════════════════════ */
function FertCalc({ state, upd, course, courseChemicals }) {
  const emptyForm = () => ({ date: todayStr(), fertId: "", fertName: "", area: course?.area || "", N: "", P: "", K: "", nRate: "", p2o5Rate: "", k2o5Rate: "", note: "" });
  const [f, setF] = useState(emptyForm);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const fertilizers = courseChemicals.filter(c => c.type === "fertilizer" || c.type === "both");
  const setField = (k, v) => setF(p => ({ ...p, [k]: v }));

  const calc = () => {
    const N = toNum(f.N), P = toNum(f.P), K = toNum(f.K);
    const nRate = toNum(f.nRate), p2o5Rate = toNum(f.p2o5Rate), k2o5Rate = toNum(f.k2o5Rate);
    const area = toNum(f.area);
    if (!area) { alert("시비 면적을 입력해 주세요"); return; }
    if (!N && !P && !K) { alert("N, P, K 중 하나 이상을 입력해 주세요"); return; }
    const p2o5 = P * P_TO_P2O5, k2o = K * K_TO_K2O;
    const fertN = nRate > 0 ? N / (nRate / 100) : null;
    const fertP = p2o5Rate > 0 ? p2o5 / (p2o5Rate / 100) : null;
    const fertK = k2o5Rate > 0 ? k2o / (k2o5Rate / 100) : null;
    setResult({ N, P, K, p2o5, k2o, nRate, p2o5Rate, k2o5Rate, area, fertN, fertP, fertK, totalN_kg: fertN !== null ? fertN * area / 1000 : null, totalP_kg: fertP !== null ? fertP * area / 1000 : null, totalK_kg: fertK !== null ? fertK * area / 1000 : null });
    setSaved(false);
  };

  const saveRecord = () => {
    if (!result || !state.selectedCourseId) return;
    const fertName = f.fertId ? (courseChemicals.find(c => c.id === f.fertId)?.name || "") : f.fertName;
    upd({ fertRecords: [...state.fertRecords, { id: genId(), courseId: state.selectedCourseId, date: f.date, fertId: f.fertId, fertName, area: result.area, N: result.N, P: result.P, K: result.K, nRate: result.nRate, p2o5Rate: result.p2o5Rate, k2o5Rate: result.k2o5Rate, p2o5: result.p2o5, k2o: result.k2o, fertN: result.fertN, fertP: result.fertP, fertK: result.fertK, totalFertN_kg: result.totalN_kg, totalFertP_kg: result.totalP_kg, totalFertK_kg: result.totalK_kg, note: f.note }] });
    setSaved(true);
  };

  return (
    <div style={S.card}>
      <SectionLabel label="🌿 시비량 계산 (N / P / K)" />
      <div style={S.formGroup}>
        <label style={S.label}>날짜</label>
        <input type="date" value={f.date} onChange={e => setField("date", e.target.value)} style={S.input} />
      </div>
      <div style={S.formGroup}>
        <label style={S.label}>비료 선택</label>
        <select value={f.fertId} onChange={e => {
          const fert = fertilizers.find(c => c.id === e.target.value);
          setF(p => ({ ...p, fertId: e.target.value, fertName: fert?.name || "", nRate: fert?.nRate || "", p2o5Rate: fert?.p2o5Rate || "", k2o5Rate: fert?.k2o5Rate || "" }));
        }} style={S.input}>
          <option value="">직접 입력</option>
          {fertilizers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {!f.fertId && <input placeholder="비료명 직접 입력" value={f.fertName} onChange={e => setField("fertName", e.target.value)} style={{ ...S.input, marginTop: 6 }} />}
      </div>
      <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 700, marginBottom: 6 }}>목표 시비량 (g/m²)</div>
      <div style={S.row3}>
        {[["N", "질소 N"], ["P", "인산 P"], ["K", "칼리 K"]].map(([k, l]) => (
          <div key={k}>
            <label style={S.label}>{l}</label>
            <input type="number" inputMode="decimal" placeholder="0" value={f[k]} onChange={e => setField(k, e.target.value)} style={S.input} />
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 700, marginBottom: 6 }}>비료 성분 함량 (%)</div>
      <div style={S.row3}>
        {[["nRate", "N %"], ["p2o5Rate", "P₂O₅ %"], ["k2o5Rate", "K₂O %"]].map(([k, l]) => (
          <div key={k}>
            <label style={S.label}>{l}</label>
            <input type="number" inputMode="decimal" placeholder="0" value={f[k]} onChange={e => setField(k, e.target.value)} style={S.input} />
          </div>
        ))}
      </div>
      <div style={S.formGroup}>
        <label style={S.label}>시비 면적 (m²)</label>
        <input type="number" inputMode="decimal" placeholder="면적 입력" value={f.area} onChange={e => setField("area", e.target.value)} style={S.input} />
      </div>
      <div style={S.formGroup}>
        <label style={S.label}>메모</label>
        <input placeholder="메모 (선택)" value={f.note} onChange={e => setField("note", e.target.value)} style={S.input} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={calc} style={S.btnPrimary}>계산하기</button>
        <button onClick={() => { setF(emptyForm()); setResult(null); setSaved(false); }} style={{ ...S.btnSecondary, whiteSpace: "nowrap" }}>초기화</button>
      </div>
      {result && (
        <div style={S.resultBox}>
          <div style={{ fontSize: 11, color: C.textSub, fontWeight: 700, marginBottom: 6 }}>성분 환산</div>
          <div style={{ fontSize: 12, color: C.textSub, marginBottom: 10, lineHeight: 1.7 }}>
            P→P₂O₅: {fmt(result.P)} × 2.27 = <b>{fmt(result.p2o5)} g/m²</b> &nbsp;
            K→K₂O: {fmt(result.K)} × 1.20 = <b>{fmt(result.k2o)} g/m²</b>
          </div>
          <div style={{ fontSize: 11, color: C.textSub, fontWeight: 700, marginBottom: 6 }}>필요 비료량</div>
          {[["N 기준", result.fertN, result.totalN_kg], ["P 기준", result.fertP, result.totalP_kg], ["K 기준", result.fertK, result.totalK_kg]].map(([l, perM2, total], i, a) => (
            <div key={l} style={{ ...S.resultRow, ...(i === a.length - 1 ? { borderBottom: "none" } : {}) }}>
              <span style={S.resultLabel}>{l}</span>
              {perM2 !== null
                ? <div style={{ textAlign: "right" }}>
                  <span style={S.resultValue}>{fmt(perM2, 2)} g/m²</span>
                  <div style={{ fontSize: 11, color: C.textMuted }}>합계: {fmt(total, 2)} kg</div>
                </div>
                : <span style={{ fontSize: 13, color: C.textFaint }}>함량 미입력</span>}
            </div>
          ))}
          <div style={{ marginTop: 12 }}>
            {!course ? <div style={{ color: C.orange, fontSize: 12, textAlign: "center" }}>골프장을 선택해야 저장할 수 있습니다</div>
              : saved ? <div style={{ color: C.primary, fontSize: 13, textAlign: "center", fontWeight: 700 }}>✅ 저장되었습니다</div>
                : <button onClick={saveRecord} style={{ ...S.btnSecondary, width: "100%" }}>이력 저장</button>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   CALC SCREEN
═══════════════════════════════════════════════════════ */
function CalcScreen({ state, upd, course, courseChemicals }) {
  const [subTab, setSubTab] = useState("spray");
  return (
    <div>
      <div style={S.subTabs}>
        {[["spray", "💧 희석액"], ["fert", "🌿 시비량"]].map(([id, label]) => (
          <button key={id} onClick={() => setSubTab(id)} style={{ ...S.subTab, ...(subTab === id ? S.subTabActive : {}) }}>{label}</button>
        ))}
      </div>
      {subTab === "spray"
        ? <SprayCalc state={state} upd={upd} course={course} courseChemicals={courseChemicals} />
        : <FertCalc state={state} upd={upd} course={course} courseChemicals={courseChemicals} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   HISTORY SCREEN
═══════════════════════════════════════════════════════ */
function HistoryScreen({ state, upd, course, courseSprayRecords, courseFertRecords }) {
  const [subTab, setSubTab] = useState("spray");
  const [filter, setFilter] = useState({ from: "", to: "" });

  if (!course) return <NoCourse />;

  const applyFilter = rs => rs.filter(r => {
    if (filter.from && r.date < filter.from) return false;
    if (filter.to && r.date > filter.to) return false;
    return true;
  }).sort((a, b) => b.date.localeCompare(a.date));

  const sprayList = applyFilter(courseSprayRecords);
  const fertList = applyFilter(courseFertRecords);

  const exportSpray = () => downloadCSV(`spray_${todayStr()}.csv`, [
    ["날짜", "농약명", "원액농도(mL/m²)", "희석배율", "면적(m²)", "원액량(L)", "물량(L)", "총희석액(L)", "메모"],
    ...sprayList.map(r => [r.date, r.chemicalName, r.concentration, r.ratio, r.area, r.origL?.toFixed(2), r.waterL?.toFixed(2), r.totalDilution?.toFixed(2), r.note]),
  ]);

  const exportFert = () => downloadCSV(`fert_${todayStr()}.csv`, [
    ["날짜", "비료명", "면적(m²)", "N", "P", "K", "N함량%", "P2O5함량%", "K2O함량%", "필요량_N(g/m²)", "필요량_P(g/m²)", "필요량_K(g/m²)", "합계_N(kg)", "합계_P(kg)", "합계_K(kg)", "메모"],
    ...fertList.map(r => [r.date, r.fertName, r.area, r.N, r.P, r.K, r.nRate, r.p2o5Rate, r.k2o5Rate, r.fertN?.toFixed(2), r.fertP?.toFixed(2), r.fertK?.toFixed(2), r.totalFertN_kg?.toFixed(2), r.totalFertP_kg?.toFixed(2), r.totalFertK_kg?.toFixed(2), r.note]),
  ]);

  const delSpray = (id) => upd({ sprayRecords: state.sprayRecords.filter(r => r.id !== id) });
  const delFert = (id) => upd({ fertRecords: state.fertRecords.filter(r => r.id !== id) });

  return (
    <div>
      <div style={S.subTabs}>
        {[["spray", "💧 살포 이력"], ["fert", "🌿 시비 이력"]].map(([id, label]) => (
          <button key={id} onClick={() => setSubTab(id)} style={{ ...S.subTab, ...(subTab === id ? S.subTabActive : {}) }}>{label}</button>
        ))}
      </div>
      <div style={{ ...S.card, padding: 12, marginBottom: 10 }}>
        <div style={S.row2}>
          <div><label style={S.label}>시작일</label><input type="date" value={filter.from} onChange={e => setFilter(p => ({ ...p, from: e.target.value }))} style={S.input} /></div>
          <div><label style={S.label}>종료일</label><input type="date" value={filter.to} onChange={e => setFilter(p => ({ ...p, to: e.target.value }))} style={S.input} /></div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 13, color: C.textMuted }}>총 {subTab === "spray" ? sprayList.length : fertList.length}건</span>
        <button onClick={subTab === "spray" ? exportSpray : exportFert} style={S.btnSecondary}>CSV 내보내기</button>
      </div>
      {subTab === "spray" ? (
        sprayList.length === 0 ? <EmptyMsg msg="살포 이력이 없습니다" /> :
          sprayList.map(r => (
            <div key={r.id} style={S.listItem}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{r.chemicalName || "미지정"}</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: C.textMuted }}>{r.date}</span>
                  <button onClick={() => delSpray(r.id)} style={{ ...S.btnDanger, padding: "3px 7px", fontSize: 11 }}>🗑</button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 6 }}>{r.concentration} mL/m² × {r.ratio}배 | {Number(r.area).toLocaleString()} m²</div>
              <div style={{ display: "flex", gap: 14, fontSize: 13, flexWrap: "wrap" }}>
                <span>원액 <b style={{ color: C.primary }}>{fmt(r.origL, 2)} L</b></span>
                <span>물 <b style={{ color: C.teal }}>{fmt(r.waterL, 1)} L</b></span>
                <span>총 <b style={{ color: C.primary }}>{fmt(r.totalDilution, 1)} L</b></span>
              </div>
              {r.note && <div style={{ fontSize: 11, color: C.textFaint, marginTop: 4 }}>{r.note}</div>}
            </div>
          ))
      ) : (
        fertList.length === 0 ? <EmptyMsg msg="시비 이력이 없습니다" /> :
          fertList.map(r => (
            <div key={r.id} style={S.listItem}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{r.fertName || "미지정"}</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: C.textMuted }}>{r.date}</span>
                  <button onClick={() => delFert(r.id)} style={{ ...S.btnDanger, padding: "3px 7px", fontSize: 11 }}>🗑</button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 6 }}>N:{r.N || 0} P:{r.P || 0} K:{r.K || 0} g/m² | {Number(r.area).toLocaleString()} m²</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 13 }}>
                {r.nRate > 0 && <span>N기준 <b style={{ color: C.primary }}>{fmt(r.fertN, 1)} g/m²</b> → {fmt(r.totalFertN_kg, 2)} kg</span>}
                {r.p2o5Rate > 0 && <span>P기준 <b style={{ color: C.teal }}>{fmt(r.fertP, 1)} g/m²</b> → {fmt(r.totalFertP_kg, 2)} kg</span>}
                {r.k2o5Rate > 0 && <span>K기준 <b style={{ color: C.orange }}>{fmt(r.fertK, 1)} g/m²</b> → {fmt(r.totalFertK_kg, 2)} kg</span>}
              </div>
              {r.note && <div style={{ fontSize: 11, color: C.textFaint, marginTop: 4 }}>{r.note}</div>}
            </div>
          ))
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   INVENTORY SCREEN
═══════════════════════════════════════════════════════ */
function InventoryScreen({ state, upd, course, courseChemicals }) {
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const EMPTY = { name: "", type: "chemical", unit: "L", stock: "", minStock: "", nRate: "", p2o5Rate: "", k2o5Rate: "", note: "" };
  const [form, setForm] = useState(EMPTY);
  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openAdd = () => { setForm(EMPTY); setEditId(null); setShowModal(true); };
  const openEdit = (item) => { setForm({ ...item }); setEditId(item.id); setShowModal(true); };
  const save = () => {
    if (!form.name.trim()) { alert("이름을 입력해 주세요"); return; }
    if (editId) upd({ chemicals: state.chemicals.map(c => c.id === editId ? { ...form, id: editId, courseId: state.selectedCourseId } : c) });
    else upd({ chemicals: [...state.chemicals, { ...form, id: genId(), courseId: state.selectedCourseId }] });
    setShowModal(false);
  };
  const del = (id) => { if (!window.confirm("삭제할까요?")) return; upd({ chemicals: state.chemicals.filter(c => c.id !== id) }); };

  if (!course) return <NoCourse />;

  const typeLabel = { chemical: "농약", fertilizer: "비료", both: "겸용" };
  const typeStyle = {
    chemical: { bg: C.blueBg, text: C.blue },
    fertilizer: { bg: C.primaryLight, text: C.primary },
    both: { bg: C.tealBg, text: C.teal },
  };
  const filtered = courseChemicals.filter(c => filterType === "all" || c.type === filterType);

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {[["all", "전체"], ["chemical", "농약"], ["fertilizer", "비료"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilterType(v)} style={{
            padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: "pointer",
            background: filterType === v ? C.primary : C.surface,
            border: `1.5px solid ${filterType === v ? C.primary : C.border}`,
            color: filterType === v ? "#fff" : C.textMuted,
          }}>{l}</button>
        ))}
        <button onClick={openAdd} style={{ ...S.btnPrimary, flex: 1, padding: "7px 12px", fontSize: 13, minWidth: 80 }}>+ 추가</button>
      </div>

      {filtered.length === 0 ? <EmptyMsg msg="등록된 농약·비료가 없습니다" /> :
        filtered.map(item => {
          const ts = typeStyle[item.type] || typeStyle.chemical;
          const isLow = item.minStock && Number(item.stock) <= Number(item.minStock);
          return (
            <div key={item.id} style={S.listItem}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>{item.name}</span>
                    <span style={{ ...S.badge, background: ts.bg, color: ts.text }}>{typeLabel[item.type] || item.type}</span>
                    {isLow && <span style={{ ...S.badge, background: C.orangeBg, color: C.orange }}>⚠️ 부족</span>}
                  </div>
                  <div style={{ fontSize: 13, display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <span>재고 <b style={{ color: isLow ? C.orange : C.primary }}>{item.stock || 0} {item.unit}</b>
                      {item.minStock ? <span style={{ color: C.textFaint }}> / 최소 {item.minStock}</span> : ""}</span>
                    {(item.type !== "chemical") && (item.nRate || item.p2o5Rate || item.k2o5Rate) &&
                      <span style={{ fontSize: 11, color: C.textMuted }}>N:{item.nRate || 0}% P₂O₅:{item.p2o5Rate || 0}% K₂O:{item.k2o5Rate || 0}%</span>}
                  </div>
                  {item.note && <div style={{ fontSize: 11, color: C.textFaint, marginTop: 4 }}>{item.note}</div>}
                </div>
                <div style={{ display: "flex", gap: 6, marginLeft: 8 }}>
                  <button onClick={() => openEdit(item)} style={{ ...S.btnSecondary, padding: "5px 10px", fontSize: 12 }}>수정</button>
                  <button onClick={() => del(item.id)} style={{ ...S.btnDanger, padding: "5px 8px" }}>🗑</button>
                </div>
              </div>
            </div>
          );
        })}

      {showModal && (
        <Modal title={editId ? "농약·비료 수정" : "농약·비료 추가"} onClose={() => setShowModal(false)}>
          <div style={S.formGroup}>
            <label style={S.label}>이름 *</label>
            <input placeholder="예: 살균제, 복합비료" value={form.name} onChange={e => setField("name", e.target.value)} style={S.input} />
          </div>
          <div style={S.row2}>
            <div>
              <label style={S.label}>종류</label>
              <select value={form.type} onChange={e => setField("type", e.target.value)} style={S.input}>
                <option value="chemical">농약</option>
                <option value="fertilizer">비료</option>
                <option value="both">농약+비료</option>
              </select>
            </div>
            <div>
              <label style={S.label}>단위</label>
              <select value={form.unit} onChange={e => setField("unit", e.target.value)} style={S.input}>
                {["L", "mL", "kg", "g", "병", "포", "개"].map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div style={S.row2}>
            <div><label style={S.label}>현재 재고</label><input type="number" inputMode="decimal" placeholder="0" value={form.stock} onChange={e => setField("stock", e.target.value)} style={S.input} /></div>
            <div><label style={S.label}>최소 재고</label><input type="number" inputMode="decimal" placeholder="0" value={form.minStock} onChange={e => setField("minStock", e.target.value)} style={S.input} /></div>
          </div>
          {(form.type === "fertilizer" || form.type === "both") && (
            <>
              <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 700, marginBottom: 6 }}>비료 성분 함량 (%)</div>
              <div style={S.row3}>
                {[["nRate", "N %"], ["p2o5Rate", "P₂O₅ %"], ["k2o5Rate", "K₂O %"]].map(([k, l]) => (
                  <div key={k}>
                    <label style={S.label}>{l}</label>
                    <input type="number" inputMode="decimal" placeholder="0" value={form[k]} onChange={e => setField(k, e.target.value)} style={S.input} />
                  </div>
                ))}
              </div>
            </>
          )}
          <div style={S.formGroup}>
            <label style={S.label}>메모</label>
            <input placeholder="메모" value={form.note} onChange={e => setField("note", e.target.value)} style={S.input} />
          </div>
          <button onClick={save} style={S.btnPrimary}>저장</button>
        </Modal>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   SETTINGS SCREEN
═══════════════════════════════════════════════════════ */
function SettingsScreen({ state, upd }) {
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", area: "" });

  const openAdd = () => { setForm({ name: "", area: "" }); setEditId(null); setShowModal(true); };
  const openEdit = (c) => { setForm({ name: c.name, area: c.area || "" }); setEditId(c.id); setShowModal(true); };
  const saveCourse = () => {
    if (!form.name.trim()) { alert("이름을 입력해 주세요"); return; }
    if (editId) upd({ courses: state.courses.map(c => c.id === editId ? { ...c, ...form } : c) });
    else { const newId = genId(); upd({ courses: [...state.courses, { id: newId, ...form }], selectedCourseId: newId }); }
    setShowModal(false);
  };
  const delCourse = (id) => {
    if (!window.confirm("골프장을 삭제하면 해당 데이터가 모두 삭제됩니다. 계속하시겠습니까?")) return;
    const next = state.courses.find(c => c.id !== id);
    upd({ courses: state.courses.filter(c => c.id !== id), chemicals: state.chemicals.filter(c => c.courseId !== id), sprayRecords: state.sprayRecords.filter(r => r.courseId !== id), fertRecords: state.fertRecords.filter(r => r.courseId !== id), gpRecords: (state.gpRecords || []).filter(r => r.courseId !== id), selectedCourseId: state.selectedCourseId === id ? (next?.id || null) : state.selectedCourseId });
  };

  const exportBackup = () => {
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `agro_backup_${todayStr()}.json`; a.click();
    URL.revokeObjectURL(url);
  };
  const importBackup = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const imported = JSON.parse(ev.target.result);
        if (window.confirm("현재 데이터가 모두 덮어씌워집니다. 계속하시겠습니까?")) upd(imported);
      } catch { alert("파일 형식이 올바르지 않습니다"); }
    };
    reader.readAsText(file); e.target.value = "";
  };

  return (
    <div>
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <SectionLabel label="⛳ 골프장 관리" />
          <button onClick={openAdd} style={{ ...S.btnPrimary, width: "auto", padding: "8px 14px", fontSize: 13 }}>+ 추가</button>
        </div>
        {state.courses.length === 0 ? (
          <div style={{ color: C.textFaint, fontSize: 13, textAlign: "center", padding: "20px 0" }}>골프장을 추가해 주세요</div>
        ) : state.courses.map(c => (
          <div key={c.id} style={{ ...S.listItem, marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>{c.name}</span>
                  {state.selectedCourseId === c.id && <span style={{ ...S.badge, background: C.primaryLight, color: C.primary }}>선택중</span>}
                </div>
                {c.area && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>면적: {Number(c.area).toLocaleString()} m²</div>}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {state.selectedCourseId !== c.id && <button onClick={() => upd({ selectedCourseId: c.id })} style={{ ...S.btnSecondary, padding: "5px 10px", fontSize: 12 }}>선택</button>}
                <button onClick={() => openEdit(c)} style={{ ...S.btnSecondary, padding: "5px 10px", fontSize: 12 }}>수정</button>
                <button onClick={() => delCourse(c.id)} style={{ ...S.btnDanger, padding: "5px 8px" }}>🗑</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={S.card}>
        <SectionLabel label="💾 데이터 관리" />
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button onClick={exportBackup} style={S.btnSecondary}>전체 백업 내보내기 (JSON)</button>
          <label style={{ ...S.btnSecondary, textAlign: "center", cursor: "pointer", display: "block" }}>
            백업 복원 (JSON)
            <input type="file" accept=".json" onChange={importBackup} style={{ display: "none" }} />
          </label>
        </div>
      </div>

      <div style={{ ...S.card, textAlign: "center" }}>
        <img src="/icons/grasslogo.png" alt="잔디 로고" style={{ width: 64, height: 64, objectFit: "contain", marginBottom: 10 }} />
        <div style={{ fontSize: 14, fontWeight: 800, color: C.primary }}>시비·시약 관리 v2.0</div>
        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>(주)서원양행</div>
      </div>

      {showModal && (
        <Modal title={editId ? "골프장 수정" : "골프장 추가"} onClose={() => setShowModal(false)}>
          <div style={S.formGroup}>
            <label style={S.label}>골프장 이름 *</label>
            <input placeholder="예: OO 골프클럽" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={S.input} />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>관리 면적 (m²)</label>
            <input type="number" inputMode="decimal" placeholder="예: 50000" value={form.area} onChange={e => setForm(p => ({ ...p, area: e.target.value }))} style={S.input} />
          </div>
          <button onClick={saveCourse} style={S.btnPrimary}>저장</button>
        </Modal>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN APP
═══════════════════════════════════════════════════════ */
export default function App() {
  const [state, setState] = useState(loadState);
  const [tab, setTab] = useState("home");

  useEffect(() => { saveState(state); }, [state]);
  const upd = useCallback((patch) => setState(s => ({ ...s, ...patch })), []);

  const course = state.courses.find(c => c.id === state.selectedCourseId);
  const courseChemicals = state.chemicals.filter(c => c.courseId === state.selectedCourseId);
  const courseSprayRecords = state.sprayRecords.filter(r => r.courseId === state.selectedCourseId);
  const courseFertRecords = state.fertRecords.filter(r => r.courseId === state.selectedCourseId);
  const courseGpRecords = (state.gpRecords || []).filter(r => r.courseId === state.selectedCourseId);

  const navItems = [
    { id: "home", icon: "🏠", label: "홈" },
    { id: "calc", icon: "🧮", label: "계산기" },
    { id: "gp", icon: "📈", label: "생장지수" },
    { id: "history", icon: "📋", label: "이력" },
    { id: "inventory", icon: "📦", label: "재고" },
    { id: "settings", icon: "⚙️", label: "설정" },
  ];

  return (
    <div style={S.app}>
      {/* Header */}
      <header style={S.header}>
        <div style={S.headerLogo}>
          <div style={{ ...S.headerIcon, padding: 0, background: "transparent", overflow: "hidden" }}>
            <img src="/icons/grasslogo.png" alt="잔디 로고" style={{ width: 36, height: 36, objectFit: "contain" }} />
          </div>
          <div>
            <div style={S.headerTitle}>시비·시약 관리</div>
            <div style={S.headerSub}>Golf Course Agro Manager</div>
          </div>
        </div>
        {state.courses.length > 0 ? (
          <select value={state.selectedCourseId || ""} onChange={e => upd({ selectedCourseId: e.target.value })} style={S.courseSelect}>
            <option value="" disabled>골프장 선택</option>
            {state.courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ) : (
          <span style={{ fontSize: 11, color: C.textFaint, cursor: "pointer" }} onClick={() => setTab("settings")}>설정 →</span>
        )}
      </header>

      {/* Main */}
      <main style={S.main}>
        {tab === "home" && <HomeScreen course={course} courseChemicals={courseChemicals} courseSprayRecords={courseSprayRecords} courseFertRecords={courseFertRecords} courseGpRecords={courseGpRecords} setTab={setTab} />}
        {tab === "calc" && <CalcScreen state={state} upd={upd} course={course} courseChemicals={courseChemicals} />}
        {tab === "gp" && <GpScreen state={state} upd={upd} course={course} />}
        {tab === "history" && <HistoryScreen state={state} upd={upd} course={course} courseSprayRecords={courseSprayRecords} courseFertRecords={courseFertRecords} />}
        {tab === "inventory" && <InventoryScreen state={state} upd={upd} course={course} courseChemicals={courseChemicals} />}
        {tab === "settings" && <SettingsScreen state={state} upd={upd} />}
      </main>

      {/* Bottom Nav */}
      <nav style={S.nav}>
        {navItems.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ ...S.navBtn, ...(tab === t.id ? { color: C.primary } : {}) }}>
            <span style={S.navIcon}>{t.icon}</span>
            <span style={{ ...S.navLabel, ...(tab === t.id ? { color: C.primary } : {}) }}>{t.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}